import { compact, defaultTo, get, isUndefined, keys, head } from 'lodash'

import { createField } from 'e-submission/domain/data-model/form/utils'
import { otherDetailFields } from 'e-submission/domain/data-model/product-form/product-investment/KYC'
import { getPartnerKYCQuestion } from 'quick-quote/selectors/partner-selector'
import { store as qqStore } from 'store'

/**
 * createESubmissionApplication
 */
export function createESubmissionProductFormInvestment() {
  const question_codes = {
    occupation: {
      question_code: 'Q4001',
      question_code_others: 'SQ4001_1',
      answer_other: 'ANS4012',
    },
    job_industry: {
      question_code: 'Q4002',
      question_code_others: 'SQ4002_1',
      answer_other: 'ANS4012',
    },
    income_source: {
      question_code: 'Q4003',
      question_code_others: 'SQ4003_1',
      answer_other: 'ANS4006',
    },
    income_per_year: {
      question_code: 'Q4004',
      question_code_others: null,
      answer_other: null,
    },
    invest_reason: {
      question_code: 'Q4005',
      question_code_others: 'SQ4005_1',
      answer_other: 'ANS4005',
    },
  }

  /**
   * convertQuestionMode
   * @param {Record<string, any>} question
   */
  function convertQuestionMode(question) {
    if (question.answerMode === 'CHECKBOX') {
      return 'MultipleCheckbox'
    } else if (question.answerMode === 'TEXT') {
      return 'TextInput'
    } else if (question.answerMode === 'RADIO') {
      return 'Radio'
    }
    return undefined
  }

  /**
   * createQuestionStructure
   * @param {Record<string, any>[]} questions
   * @param {string} code
   */
  function get_question(questions, code) {
    if (isUndefined(questions) || questions.length === 0) {
      return undefined
    }
    return questions.find((q) => code === q.questionCode)
  }

  /**
   * createFieldSection
   * @param {string} fieldId
   * @param {Record<string, any>} question_main
   * @param {Record<string, any>} question_others
   */
  function createFieldSection(fieldId, question_main, question_others) {
    if (isUndefined(question_main)) {
      return []
    }

    return compact([
      createField(fieldId, [
        {
          id: fieldId,
          label: question_main.questionNameTH,
          c: convertQuestionMode(question_main),
          required: question_main.requiredFieldFlag,
          p: {
            options: question_main.answers?.map((a) => {
              return {
                text: a.answerChoiceTH,
                value: a.answerCode,
              }
            }),
          },
        },
      ]),
      // others
      !isUndefined(question_others)
        ? otherDetailFields(fieldId, question_others.questionNameTH, question_others.requiredFieldFlag)
        : undefined,
    ])
  }

  function occupationSection(app, id, section) {
    const kyc_question = getPartnerKYCQuestion(qqStore.getState())
    const fieldId = `${id}.occupation`

    const { question_code, answer_other, question_code_others } = question_codes.occupation

    const isSelectedOthers = keys(defaultTo(get(app, fieldId), {})).includes(answer_other)
    return createFieldSection(
      fieldId,
      get_question(kyc_question, question_code),
      isSelectedOthers ? get_question(kyc_question, question_code_others) : undefined
    )
  }

  function jobIndustrySection(app, id, section) {
    const kyc_question = getPartnerKYCQuestion(qqStore.getState())
    const fieldId = `${id}.jobIndustry`

    const { question_code, answer_other, question_code_others } = question_codes.job_industry

    const isSelectedOthers = keys(defaultTo(get(app, fieldId), {})).includes(answer_other)
    return createFieldSection(
      fieldId,
      get_question(kyc_question, question_code),
      isSelectedOthers ? get_question(kyc_question, question_code_others) : undefined
    )
  }

  function incomeSourceSection(app, id, section) {
    const kyc_question = getPartnerKYCQuestion(qqStore.getState())
    const fieldId = `${id}.incomeSource`

    const { question_code, answer_other, question_code_others } = question_codes.income_source

    const isSelectedOthers = keys(defaultTo(get(app, fieldId), {})).includes(answer_other)
    return createFieldSection(
      fieldId,
      get_question(kyc_question, question_code),
      isSelectedOthers ? get_question(kyc_question, question_code_others) : undefined
    )
  }

  function investReasonSection(app, id, section) {
    const kyc_question = getPartnerKYCQuestion(qqStore.getState())
    const fieldId = `${id}.investReason`

    const { question_code, answer_other, question_code_others } = question_codes.invest_reason

    const isSelectedOthers = keys(defaultTo(get(app, fieldId), {})).includes(answer_other)
    return createFieldSection(
      fieldId,
      get_question(kyc_question, question_code),
      isSelectedOthers ? get_question(kyc_question, question_code_others) : undefined
    )
  }

  function incomePerYearField(app, id, section) {
    const kyc_question = getPartnerKYCQuestion(qqStore.getState())
    const fieldId = `${id}.incomePerYear`

    const { question_code } = question_codes.income_per_year

    return head(createFieldSection(fieldId, get_question(kyc_question, question_code), undefined))
  }

  return {
    occupationSection,
    jobIndustrySection,
    incomeSourceSection,
    investReasonSection,
    incomePerYearField,
  }
}
